import { isEmpty, range } from 'lodash';
import moment from 'moment';
import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import URI from 'urijs';
import { Modal } from 'Components/frameworks/modal/Modal';
import { ICONS } from 'Components/utils/icon/icons';
import { OPEN_CRITERIA_WHEN_EVENT } from 'Components/widgets/criteria/criteria-when/constants';
import { FilterWhen } from 'Components/widgets/criteria/criteria-when/filter-when/FilterWhen';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import {
  ACTIONS,
  useCriteriaDisplayContext,
} from 'Components/widgets/criteria/CriteriaDisplayContext';
import { CriteriaTrigger } from 'Components/widgets/criteria/CriteriaTrigger';
import { DayPriceDoubleSeasonPopin } from 'Components/widgets/trip-date-picker/day-with-price/DayPriceDoubleSeasonPopin';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { setIsPriceCalendarSelection } from 'Store/calendar/calendar.action';
import type { PricesByDays } from 'Store/calendar/calendar.helper';
import 'Store/calendar/calendar.helper';
import { getInitialDuration } from 'Store/calendar/calendar.selector';
import {
  changeTripDuration,
  clearDepartureDate,
  clearTripDuration,
} from 'Store/faceting/facetingSearch.action';
import { useDelayUnmount } from 'Src/hooks/use-unmount-delay/useUnmountDelay';
import { useBlockInputDepartureDate } from 'Src/hooks/use-block-input-departure-date/useBlockInputDepartureDate';
import { setDepartureDate } from 'Store/trip-criteria/departure-date/departureDate.action';
import { DEFAULT_TRIP_DURATION } from 'Store/trip-criteria/duration/duration.constants';
import { setFlexibility } from 'Store/trip-criteria/flexibility/flexibility.action';
import { getTripFlexibility } from 'Store/trip-criteria/tripCriteria.selector';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { timestampToApiDate } from 'Utils/date/apiDate';
import { QUERY_DATE_FORMAT, QUERY_DEPARTURE_DATE, QUERY_DURATION } from 'Utils/uri/constants';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import type { ButtonProps } from 'Components/frameworks/button/Button';
import { FlexibilityBlock } from './FlexibilityBlock';
import { CriteriaWhenReassuranceDropdown } from './CriteriaWhenReassuranceDropdown';
type CriteriaWhenProps = {
  defaultDuration?: number;
  durationOptions: Array<number>;
  isDropDownRelative?: boolean;
  isJoyview?: boolean;
  offsetSell?: number | null | undefined;
  prices?: PricesByDays;
  product?: Product;
  checkIfDepartureDateIsBookable?: boolean;
  withFlexibility?: boolean;
  theme?: string;
  useQueryParams?: boolean;
};

const selector = (state: ReduxState) => {
  const tripCriteria = state.tripCriteria as {
    departureDate: number | null | undefined;
    duration: number | null | undefined;
  };
  return {
    departureDate: tripCriteria.departureDate,
    duration: tripCriteria.duration,
  };
};

export const CriteriaWhen = (props: CriteriaWhenProps) => {
  const appContext = useAppContext();
  const [criteriaContext, setCriteriaContext] = useContext(CriteriaContext);
  const [criteriaDisplayContext, dispatchCriteriaDisplayContext] = useCriteriaDisplayContext();
  const { departureDate, duration } = useSelector(selector);
  const intialDuration = useSelector(getInitialDuration);
  const {
    checkIfDepartureDateIsBookable,
    isDropDownRelative = false,
    prices,
    product,
    withFlexibility,
    theme = 'default',
    useQueryParams,
  } = props;
  const { blockInputDepartureDate, setBlockInputDepartureDate } = useBlockInputDepartureDate(
    checkIfDepartureDateIsBookable,
  );
  const { isAppMounted, isRTL } = useResponsive();
  const { asPath, replace } = useRouter();
  const seasonLabel =
    product?.isSummer || product?.isWinter
      ? `booking.priceCalendar.priceStatus.${product?.isWinter ? 'siblingSummer' : 'siblingWinter'}`
      : '';
  const [inputDepartureDate, setInputDepartureDate] = useState(
    !blockInputDepartureDate ? departureDate : null,
  );
  const flexibility = useSelector<number>(getTripFlexibility);
  const [inputDuration, setInputDuration] = useState(duration);
  const dispatch = useDispatch();
  const track = useTracking({
    actionGA: criteriaContext.trackingActionName,
  });
  const [isDoubleSeasonPopinVisible, setIsDoubleSeasonPopinVisible] = useState(false);

  const onFlexibilityChange = (value: number) => {
    const flexibilityValue = value !== flexibility ? value : 0;
    track({
      actionGA: 'smartCriterias',
      labelGA: `flexibility_${flexibilityValue || 'none'}`,
    });
    dispatch(setFlexibility(flexibilityValue));
  };

  const showFlexibility = withFlexibility && !!inputDepartureDate && !!inputDuration;
  const [shouldDisplayFlexibility, setShouldDisplayFlexibility] = useState(showFlexibility);
  useEffect(() => {
    if (!shouldDisplayFlexibility && showFlexibility) {
      setShouldDisplayFlexibility(true);
    } else if (shouldDisplayFlexibility && !showFlexibility) {
      setShouldDisplayFlexibility(false);
    }
  }, [showFlexibility]);
  const shouldRenderFlexibility = useDelayUnmount(!!shouldDisplayFlexibility, 300);
  const flexibilityBlock = (
    <FlexibilityBlock
      shouldDisplayFlexibility={shouldDisplayFlexibility}
      flexibility={flexibility}
      onFlexibilityChange={onFlexibilityChange}
    />
  );
  useEffect(() => {
    const formattedDepartureDate = departureDate
      ? moment(departureDate).format('DDMM')
      : departureDate;
    const formattedInputDepartureDate = inputDepartureDate
      ? moment(inputDepartureDate).format('DDMM')
      : inputDepartureDate;

    if (formattedDepartureDate !== formattedInputDepartureDate && !blockInputDepartureDate) {
      setInputDepartureDate(departureDate);
    }
  }, [departureDate]);
  useEffect(() => {
    if (duration !== inputDuration) {
      setInputDuration(duration);
    }
  }, [duration]);
  useEffect(() => {
    setCriteriaContext({
      criteriaWhenIsSet: Boolean(departureDate && duration && !blockInputDepartureDate),
    });
  }, [departureDate, duration, blockInputDepartureDate]);
  useEffect(() => {
    setInputDepartureDate(blockInputDepartureDate ? null : departureDate);
  }, [blockInputDepartureDate]);

  const openCriteriaEventHandler = () => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaWhen',
    });
  };

  useEffect(() => {
    document.addEventListener(OPEN_CRITERIA_WHEN_EVENT, openCriteriaEventHandler);
    return () => document.removeEventListener(OPEN_CRITERIA_WHEN_EVENT, openCriteriaEventHandler);
  }, []);
  const hasPrices = !isEmpty(prices);
  const apiDate = timestampToApiDate(departureDate);
  const price = hasPrices && !!apiDate ? prices[apiDate] : undefined;
  const priceAvailability = price ? price?.availability : '';

  const setUrlQueries = (queries: Record<string, string | number>) => {
    const uri = new URI(asPath);
    const searchPageUrl = uri.clone().setQuery(queries);
    replace(searchPageUrl.toString(), undefined, {
      shallow: true,
      scroll: false,
    });
  };

  const saveDateAndDuration = () => {
    if (inputDepartureDate !== departureDate) {
      criteriaContext.setFacetHasSeeMore(false);
    }

    if (!inputDepartureDate) {
      if (useQueryParams) {
        setUrlQueries({
          [QUERY_DEPARTURE_DATE]: undefined,
          [QUERY_DURATION]: undefined,
        });
      }

      dispatch(clearDepartureDate());
      dispatch(clearTripDuration());
    } else if (departureDate) {
      if (hasPrices) {
        setIsDoubleSeasonPopinVisible(price?.siblingOpen);
      }

      const currentDuration = inputDuration || props.defaultDuration || DEFAULT_TRIP_DURATION;

      if (currentDuration !== duration) {
        if (useQueryParams) {
          setUrlQueries({
            [QUERY_DEPARTURE_DATE]: moment(departureDate).format(QUERY_DATE_FORMAT),
            [QUERY_DURATION]: currentDuration,
          });
        }

        dispatch(changeTripDuration(currentDuration));
      }
    }

    dispatchCriteriaDisplayContext({
      type: ACTIONS.CLOSE_CRITERIA,
      payload: 'criteriaWhen',
    });
  };

  const labelPartsMobile = [];

  if (isAppMounted && departureDate && duration && !blockInputDepartureDate) {
    labelPartsMobile.push(moment(departureDate).format('DD/MM'));
    const arrival = moment(departureDate).add({
      days: duration,
    });
    labelPartsMobile.push(arrival.format('DD/MM'));
  }

  const labelParts = [t('faceting.when.arrivalDateTitle'), t('faceting.when.departureDateTitle')];

  if (departureDate && (!duration || !blockInputDepartureDate)) {
    labelParts[0] = moment(departureDate).format('DD MMM');
  }

  if (departureDate && duration && !blockInputDepartureDate) {
    labelParts[1] = moment(departureDate)
      .add({
        days: duration,
      })
      .format('DD MMM');

    if (flexibility && withFlexibility) {
      labelParts[1] = `${labelParts[1]} (${t(`faceting.flexibility.duration_${flexibility}short`)})`;
    }
  }

  const durationOptions = appContext.isSeller ? range(1, 31) : props.durationOptions;
  const showDuration = !!inputDepartureDate && !!duration && !withFlexibility;
  const buttonLabel = (
    <Fragment>
      <span className="hidden flex-1 truncate lg:inline">
        {labelParts.join(' - ')}{' '}
        {showDuration &&
          `(${t('booking.form.selectDuration', {
            duration,
          })})`}
      </span>
      {labelPartsMobile.length > 1 && (
        <Fragment>
          <div className="flex h-full flex-col justify-center text-12 leading-tight lg:hidden">
            {labelPartsMobile.map((labelSplited, key) => (
              <span key={`${labelSplited}_${key}`}>{labelSplited}</span>
            ))}
          </div>
          {!!flexibility && withFlexibility && (
            <span className="lg:hidden">
              {t(`faceting.flexibility.duration_${flexibility}short`)}
            </span>
          )}
        </Fragment>
      )}
    </Fragment>
  );

  const trackDepartureDateChange = (value: moment$Moment) => {
    if (criteriaContext.trackingActionName) {
      track({
        labelGA: `when_${value.format('DD/MM/YYYY')}`,
      });
    }
  };

  const trackDurationChange = (value: number) => {
    if (criteriaContext.trackingActionName) {
      const labelGA = `duration_${value}`;
      track({
        labelGA: labelGA,
      });
    }
  };

  const closeModal = () => {
    setIsDoubleSeasonPopinVisible(false);
  };

  const offsetCalendar = appContext.isSeller ? 0 : props.offsetSell;
  const onOpen = useCallback(() => {
    dispatchCriteriaDisplayContext({
      type: ACTIONS.OPEN_CRITERIA,
      payload: 'criteriaWhen',
    });
  }, []);
  const onClear = useCallback(() => {
    if (useQueryParams) {
      setUrlQueries({
        [QUERY_DEPARTURE_DATE]: undefined,
        [QUERY_DURATION]: undefined,
      });
    }

    dispatch(clearTripDuration());
    dispatch(clearDepartureDate());
  }, []);
  const dateFormat = t('booking.form.dateFormat');
  const from = inputDepartureDate ? moment(inputDepartureDate) : null;
  const to =
    from && inputDuration
      ? moment(from).add({
          days: inputDuration,
        })
      : null;
  const layerHeader = (
    <span className="font-bold">
      {from ? from.format(dateFormat) : t('faceting.when.arrivalDateTitle')}
      {' — '}
      {to ? to.format(dateFormat) : t('faceting.when.departureDateTitle')}
    </span>
  );
  const changeDepartureDate = useCallback(
    (value: Date | null | undefined) => {
      if (!value) {
        setInputDepartureDate(null);
        setInputDuration(null);
        return;
      }

      dispatch(setIsPriceCalendarSelection(false));
      const newDepartureDate = moment(value);
      const timestamp = newDepartureDate.valueOf();

      if (inputDepartureDate !== timestamp) {
        if (blockInputDepartureDate) {
          setBlockInputDepartureDate(false);
        }

        dispatch(setDepartureDate(timestamp));
        dispatch(clearTripDuration());
        setInputDepartureDate(timestamp);
        setInputDuration(null);
        setCriteriaContext({
          defaultValueForCalendar: newDepartureDate,
        });
        trackDepartureDateChange(newDepartureDate);
      }
    },
    [inputDepartureDate],
  );
  return (
    <Fragment>
      <CriteriaTrigger
        buttonLabel={buttonLabel}
        buttonIcon={ICONS.DATE}
        buttonIsFilled={criteriaContext.criteriaWhenIsSet}
        dropDownStyle={{
          width: 'auto',
          minWidth: '48.75rem',
          transform: 'none',
          ...(isRTL
            ? {
                right: '0',
                left: 'auto',
              }
            : {
                left: '0',
                right: 'auto',
              }),
        }}
        isDropDownRelative={isDropDownRelative}
        isOpen={criteriaDisplayContext.criteriaWhenIsOpen}
        isJoyview={props.isJoyview}
        trackingLabel="when"
        onOpen={onOpen}
        onClose={saveDateAndDuration}
        onClear={onClear}
        layerHeader={layerHeader}
        layerHeaderSlot={
          hasPrices && (
            <CriteriaWhenReassuranceDropdown
              nbNightsProps={{
                from,
                to,
                duration: inputDuration,
              }}
            />
          )
        }
        buttons={[
          {
            children: t('faceting.reset'),
            className: 'pe-10',
            'data-testid': 'criteria-when-reset',
            onClick: () => {
              setInputDuration(null);
              setInputDepartureDate(null);

              if (flexibility) {
                dispatch(setFlexibility(0));
              }
            },
            theme: 'link',
          } as ButtonProps & { 'data-testid'?: string },
          {
            children: t('faceting.validate'),
            'data-testid': 'criteria-when-validate',
            onClick: () => {
              saveDateAndDuration();
              dispatchCriteriaDisplayContext({
                type: ACTIONS.OPEN_NEXT_CRITERIA,
                payload: {
                  currentCriteria: 'criteriaWhen',
                  criteriaContext,
                },
              });
            },
          } as ButtonProps & { 'data-testid'?: string },
        ]}
        buttonsSlot={shouldRenderFlexibility && flexibilityBlock}
      >
        <FilterWhen
          defaultDuration={intialDuration || props.defaultDuration}
          optionsDuration={durationOptions}
          prices={prices}
          changeTripDuration={(time) => {
            if (time) {
              setInputDuration(time);
              trackDurationChange(time);
            }
          }}
          changeDepartureDate={changeDepartureDate}
          duration={inputDuration}
          departureDate={inputDepartureDate}
          openCalendarToDate={criteriaContext.defaultValueForCalendar}
          offsetCalendar={offsetCalendar}
          dateFormat={dateFormat}
          seasonLabel={seasonLabel}
          theme={theme}
        />
      </CriteriaTrigger>
      {isDoubleSeasonPopinVisible && !!product && (
        <Modal closeLabel={t('common.close')} onClose={closeModal} size="small">
          <DayPriceDoubleSeasonPopin
            availability={priceAvailability}
            product={product}
            statusMessageKey={`booking.priceCalendar.priceStatus.${priceAvailability}`}
            trackingData={{
              actionGA: 'priceBar',
            }}
          />
        </Modal>
      )}
    </Fragment>
  );
};
